import React, { useCallback, useMemo, useState } from 'react';
import Select from 'react-select';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import RouteIcon from '@material-ui/icons/CallSplit';
import GroupIcon from '@material-ui/icons/Group';
import EventIcon from '@material-ui/icons/Event';
import AssignmentIcon from '@material-ui/icons/Assignment';
import SaveIcon from '@material-ui/icons/Save';

import { IFormOption } from '../../_core/_ui/forms.component';
import { CardButton, CardButtonIcon } from '../../_core/_ui/buttons.component';
import { SectionHeading, Text } from '../../_core/_ui/typography.component';
import { AssessmentStageBuilderItem } from './assessment-stage-builder-item.component';
import { SaveFormatTemplateModalContainer } from '../../FormatTemplate/SaveFormatTemplateModal/save-format-template-modal.container';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { isContiguousSelection, wouldBreakContiguity } from '../../../lib/helpers/format-template.helpers';
import { messages } from './assessment-stage-builder.messages';

interface IAssessmentStageBuilder {
  stages: IAssessmentStage[];
  drillOptions: IFormOption[];
  practicePlanOptions: IFormOption[];
  ageGroupOptions: IFormOption[];
  positionOptions: IFormOption[];
  setAgeGroupId: Function;
  ageGroup?: IAgeGroup | null;
  companyId: string;
  completeStage: Function;
  createStage: Function;
  updateStage: Function;
  removeStage: Function;
  moveStage: (stage: IAssessmentStage, direction: 'UP' | 'DOWN') => Promise<void>;
  isChangingStructure: boolean;
  structureError: string;
  loading: boolean;
  fetchError: boolean;
  onOpenUseTemplate: () => void;
}

export const AssessmentStageBuilder: React.FC<IAssessmentStageBuilder> = ({
  stages,
  setAgeGroupId,
  ageGroupOptions,
  ageGroup,
  companyId,
  completeStage,
  createStage,
  updateStage,
  removeStage,
  moveStage,
  isChangingStructure,
  structureError,
  loading,
  fetchError,
  onOpenUseTemplate,
  ...rest
}) => {
  const termLookup = useCompanySportTerminology();
  const athletesTerm = pluralTerm(termLookup[SportTerminology.ATHLETE]);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [selectionError, setSelectionError] = useState<React.ReactNode>(null);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);

  const exitSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedIndices([]);
    setSelectionError(null);
  }, []);

  const enterSelection = useCallback(() => {
    setSelectedIndices([]);
    setSelectionError(null);
    setSelectionMode(true);
  }, []);

  const toggleSelect = useCallback((index: number) => {
    setSelectionError(null);
    setSelectedIndices(prev => (prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]));
  }, []);

  const handleCreate = useCallback(() => {
    if (!selectedIndices.length) return setSelectionError(<IntlMsg {...messages.selectAtLeastOne} />);
    if (!isContiguousSelection(selectedIndices)) return setSelectionError(<IntlMsg {...messages.selectContiguous} />);
    setSelectionError(null);
    setNameDialogOpen(true);
  }, [selectedIndices]);

  const handleSaved = useCallback(() => {
    setNameDialogOpen(false);
    exitSelection();
  }, [exitSelection]);

  // Drop stale indices: `stages` can shrink under a refetch/apply, leaving a selected index
  // out of range. filter(Boolean) keeps the save working on the still-valid stages.
  const selectedStages = useMemo(
    () => [...selectedIndices].sort((a, b) => a - b).map(i => stages[i]).filter(Boolean),
    [selectedIndices, stages],
  );

  if (fetchError && !ageGroupOptions.length && !stages.length) return <Typography>Something went wrong. Please try again later</Typography>;
  if (loading && !ageGroupOptions.length) return <Typography>Loading...</Typography>;
  let sessionNum = 0;

  return (
    <div>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <Box display="flex" alignItems="center">
          <Typography>Select age group</Typography>
          <Box width="220px" ml={2}>
            <Select
              options={ageGroupOptions}
              onChange={option => option && setAgeGroupId(option.value)}
              value={(ageGroup && ageGroupOptions.find(a => a.value === ageGroup._id)) || null}
              isDisabled={selectionMode || isChangingStructure}
            />
          </Box>
        </Box>
        {ageGroup?._id && stages.length > 0 && (
          selectionMode ? (
            <Box display="flex" alignItems="center">
              {selectionError && <FormHelperText error style={{ marginRight: 8 }}>{selectionError}</FormHelperText>}
              <Button size="small" onClick={exitSelection}><IntlMsg {...messages.cancel} /></Button>
              <Button size="small" color="primary" variant="contained" onClick={handleCreate} style={{ marginLeft: 8 }}>
                <IntlMsg {...messages.submit} />
              </Button>
            </Box>
          ) : (
            <Button size="small" startIcon={<SaveIcon />} onClick={enterSelection} style={{ marginLeft: 8 }}>
              <IntlMsg {...messages.createTemplate} />
            </Button>
          )
        )}
      </Box>
      {selectionMode && (
        <Box mt={1}>
          <Typography variant="subtitle2"><IntlMsg {...messages.selectStagesInstruction} /></Typography>
        </Box>
      )}
      {structureError && <Typography color="error" role="alert">{structureError}</Typography>}
      {stages.map((stage, index) => {
        if (stage.type === 'SESSION') sessionNum += 1;

        return (
          <AssessmentStageBuilderItem
            sessionNum={sessionNum}
            key={stage._id}
            stage={stage}
            stages={stages}
            index={index}
            completeStage={completeStage}
            createStage={createStage}
            updateStage={updateStage}
            moveStage={moveStage}
            isChangingStructure={isChangingStructure}
            removeStage={removeStage}
            ageGroup={ageGroup}
            selectionMode={selectionMode}
            isSelected={selectedIndices.includes(index)}
            isSelectionDisabled={selectionMode && wouldBreakContiguity(selectedIndices, index)}
            onToggleSelect={toggleSelect}
            {...rest}
          />
        );
      })}
      {ageGroup?._id && (
        <>
          <Box my={2}><Divider /></Box>
          <Box my={2}><Typography variant="h6">Schedule Builder</Typography></Box>

          {/* Locked while selecting stages so the schedule can't change under the positional selection. */}
          <Grid container spacing={2} style={selectionMode || isChangingStructure ? { pointerEvents: 'none', opacity: 0.5 } : undefined}>
            <Grid item xs={12} md={3}>
              <CardButton aria-disabled={isChangingStructure} onClick={() => createStage('GROUP')}>
                <CardButtonIcon><GroupIcon color="primary" /></CardButtonIcon>
                <SectionHeading>Sort and Group {athletesTerm}</SectionHeading>
                <Text>({termLookup[SportTerminology.ATHLETE]} Organization)</Text>
              </CardButton>
            </Grid>
            <Grid item xs={12} md={3}>
              <CardButton aria-disabled={isChangingStructure} onClick={() => createStage('SESSION')}>
                <CardButtonIcon><EventIcon color="primary" /></CardButtonIcon>
                <SectionHeading>Configure an {termLookup[SportTerminology.SESSION]}</SectionHeading>
                <Text>(Evaluation Setup)</Text>
              </CardButton>
            </Grid>
            <Grid item xs={12} md={3}>
              <CardButton aria-disabled={isChangingStructure} onClick={() => createStage('ROUTE')}>
                <CardButtonIcon><RouteIcon color="primary" /></CardButtonIcon>
                <SectionHeading>Route {athletesTerm}</SectionHeading>
                <Text>(Hold or Conclude {termLookup[SportTerminology.ATHLETE]} Evaluations)</Text>
              </CardButton>
            </Grid>
            <Grid item xs={12} md={3}>
              <CardButton onClick={onOpenUseTemplate}>
                <CardButtonIcon><AssignmentIcon color="primary" /></CardButtonIcon>
                <SectionHeading><IntlMsg {...messages.useTemplate} /></SectionHeading>
                <Text><IntlMsg {...messages.useTemplateSubtitle} /></Text>
              </CardButton>
            </Grid>
          </Grid>
        </>
      )}
      {companyId && (
        <SaveFormatTemplateModalContainer
          open={nameDialogOpen}
          onClose={() => setNameDialogOpen(false)}
          onSaved={handleSaved}
          selectedStages={selectedStages}
          companyId={companyId}
          ageGroup={ageGroup}
        />
      )}
    </div>
  );
};
